import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, X, Upload, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = {
  name: "", tagline: "", duration: "", price: "", difficulty: "Easy",
  max_group: 10, description: "", highlights: "", time_slots: "", image: "", active: true,
};

export default function ToursManager() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteSlug, setDeleteSlug] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/admin/tours");
      setTours(res.data);
    } catch (e) { /* handled by parent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditingSlug(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingSlug(t.slug);
    setForm({
      name: t.name, tagline: t.tagline, duration: t.duration, price: String(t.price),
      difficulty: t.difficulty, max_group: t.max_group, description: t.description,
      highlights: (t.highlights || []).join("\n"),
      time_slots: (t.time_slots || []).join("\n"),
      image: t.image, active: t.active,
    });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.duration || !form.price || !form.description || !form.image) {
      toast.error("Name, duration, price, description and image are required.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      tagline: form.tagline,
      duration: form.duration,
      price: parseFloat(form.price),
      difficulty: form.difficulty,
      max_group: parseInt(form.max_group) || 10,
      description: form.description,
      highlights: form.highlights.split("\n").map((h) => h.trim()).filter(Boolean),
      time_slots: form.time_slots.split("\n").map((h) => h.trim()).filter(Boolean),
      image: form.image,
      active: form.active,
    };
    try {
      if (editingSlug) {
        await api.patch(`/admin/tours/${editingSlug}`, payload);
        toast.success("Tour updated");
      } else {
        await api.post("/admin/tours", payload);
        toast.success("Tour created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/tours/${deleteSlug}`);
      toast.success("Tour deleted");
      setDeleteSlug(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="tours-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{tours.length} tours</p>
        <button onClick={openCreate} data-testid="add-tour-button"
          className="flex items-center gap-2 bg-signal text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-void transition-colors duration-200">
          <Plus size={15} /> Add Tour
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="mx-auto animate-spin text-signal" /></div>
      ) : (
        <div className="border-t border-l border-border grid md:grid-cols-2" data-testid="admin-tours-grid">
          {tours.map((t) => (
            <div key={t.slug} className="border-b border-r border-border p-6" data-testid={`admin-tour-${t.slug}`}>
              <div className="flex gap-4">
                <img src={t.image} alt={t.name} className="w-24 h-20 object-cover bg-secondary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold text-lg leading-tight">{t.name}</h3>
                    {!t.active && <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-border px-2 py-0.5">Hidden</span>}
                  </div>
                  <p className="text-muted-foreground text-sm truncate">{t.tagline}</p>
                  <div className="flex items-center gap-4 mt-2 font-mono text-xs text-muted-foreground">
                    <span className="font-display font-bold text-base text-void">${t.price.toFixed(0)}</span>
                    <span>{t.duration}</span>
                    <span>{t.difficulty}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEdit(t)} data-testid={`edit-tour-${t.slug}`}
                  className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-secondary transition-colors">
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => setDeleteSlug(t.slug)} data-testid={`delete-tour-${t.slug}`}
                  className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest text-destructive hover:bg-destructive/5 transition-colors">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tour-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">
              {editingSlug ? "Edit Tour" : "New Tour"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tour-name" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Tagline</Label>
              <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="tour-tagline" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Duration *</Label>
              <Input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="1.5 hours" data-testid="tour-duration" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Price (CAD) *</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="tour-price" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Difficulty</Label>
              <Input value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} placeholder="Easy" data-testid="tour-difficulty" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Max group</Label>
              <Input type="number" value={form.max_group} onChange={(e) => set("max_group", e.target.value)} data-testid="tour-maxgroup" className="rounded-none mt-1.5 focus-visible:ring-signal" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Tour Image *</Label>
              <div className="flex gap-4 mt-1.5">
                <div className="w-32 h-24 shrink-0 border border-border bg-secondary overflow-hidden flex items-center justify-center">
                  {form.image
                    ? <img src={form.image} alt="preview" className="w-full h-full object-cover" data-testid="tour-image-preview" />
                    : <ImageIcon size={22} className="text-muted-foreground" />}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-mono uppercase tracking-widest cursor-pointer hover:bg-secondary transition-colors" data-testid="upload-image-label">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="tour-image-input" />
                  </label>
                  <p className="text-xs text-muted-foreground mt-2 font-mono">JPG, PNG or WEBP · max 8MB</p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Description *</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="tour-description" className="rounded-none mt-1.5 focus-visible:ring-signal" rows={3} />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Highlights (one per line)</Label>
              <Textarea value={form.highlights} onChange={(e) => set("highlights", e.target.value)} data-testid="tour-highlights" className="rounded-none mt-1.5 focus-visible:ring-signal" rows={3} placeholder={"Scenic river trails\nHelmet included"} />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Tour times (one per line)</Label>
              <Textarea value={form.time_slots} onChange={(e) => set("time_slots", e.target.value)} data-testid="tour-timeslots" className="rounded-none mt-1.5 focus-visible:ring-signal" rows={3} placeholder={"9:00 AM\n2:00 PM\n6:30 PM (Sunset)"} />
              <p className="text-xs text-muted-foreground mt-1.5 font-mono">These are the bookable start times shown to customers.</p>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="tour-active" />
              <Label className="font-mono text-xs uppercase tracking-widest">Visible on website</Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setOpen(false)} className="px-5 py-3 text-xs font-mono uppercase tracking-widest border border-border hover:bg-secondary transition-colors">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-tour-button"
              className="px-6 py-3 text-xs font-bold uppercase tracking-widest bg-signal text-white hover:bg-void transition-colors disabled:opacity-60 flex items-center gap-2">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save Tour"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteSlug} onOpenChange={(o) => !o && setDeleteSlug(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Delete this tour?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the tour from your site. Existing bookings are unaffected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-tour" className="rounded-none bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
